import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import db from '@/db';
import { Badge } from '@/components/ui/badge';
import { Clock, CheckCircle, ChefHat, Bike, AlertTriangle, Volume2, VolumeX, Printer, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { playDing, playNewOrderAlert, unlockAudio, installAutoUnlock } from '@/lib/sound';
import { autoPrintKitchenLabel, printKitchenLabel } from '@/lib/printSmart';
import { useVisiblePolling } from '@/hooks/useVisiblePolling';
import { useKeepAlive } from '@/lib/keepAlive';

const SOUND_STORAGE_KEY = 'kaven_kds_sound_enabled';

function getWaitMinutes(createdAt) {
  if (!createdAt) return 0;
  return Math.floor((Date.now() - new Date(createdAt).getTime()) / 60000);
}

function getUrgencyColor(minutes) {
  if (minutes < 10) return 'text-emerald-600 bg-emerald-50 border-emerald-200';
  if (minutes < 20) return 'text-amber-600 bg-amber-50 border-amber-200';
  return 'text-red-600 bg-red-50 border-red-200';
}

export default function KDSPage() {
  const [queue, setQueue] = useState([]);
  // v8.05 — keep-alive: tela ligada + heartbeat em Worker + resync ao acordar
  useKeepAlive(['kdsQueue', 'deliveryOrders']);
  // v8.04 — trava anti-duplo-clique do "Pronto": ids em processamento
  const [processingIds, setProcessingIds] = useState(() => new Set());
  const [grouped, setGrouped] = useState({});
  const [_tick, setTick] = useState(0); // força re-render a cada 30s para recalcular urgência
  // Fase 12 - Alerta sonoro para novos pedidos de delivery
  const knownDeliveryIdsRef = useRef(null); // Set<queueId>, null = primeiro load
  // v7.68 — mapa id→status pra detectar novos itens de MESA e mudanças de
  // status vindas de outro device; guard de 3s suprime ding das ações locais.
  const knownStatusRef = useRef(null);
  const localActionAtRef = useRef(0);
  const [soundEnabled, setSoundEnabled] = useState(() => {
    try {
      const v = localStorage.getItem(SOUND_STORAGE_KEY);
      return v === null ? true : v === 'true';
    } catch {
      return true;
    }
  });

  const loadQueue = useCallback(async () => {
    const items = await db.kdsQueue.where('status').anyOf(['pending', 'preparing']).toArray();

    // Detecta novos pedidos vindos do delivery (robô) e toca ding
    const deliveryIds = new Set(
      items.filter((i) => i.source === 'delivery').map((i) => i.id)
    );
    if (knownDeliveryIdsRef.current !== null) {
      // Não toca no primeiro load; só em mudanças subsequentes
      let newOnes = 0;
      const newlyArrivedQueueItems = [];
      for (const id of deliveryIds) {
        if (!knownDeliveryIdsRef.current.has(id)) {
          newOnes += 1;
          const qItem = items.find((i) => i.id === id);
          if (qItem) newlyArrivedQueueItems.push(qItem);
        }
      }
      if (newOnes > 0 && soundEnabled) {
        playNewOrderAlert();
        toast.success(`🔔 ${newOnes} novo${newOnes > 1 ? 's' : ''} pedido${newOnes > 1 ? 's' : ''} de delivery!`);
      }
      // Fase 22 — Auto-print modo-aware.
      // webserial: imprime direto (sem prompt) se porta autorizada.
      // windows: NÃO auto-imprime (evita dialog intrusivo a cada pedido).
      // Cozinheiro usa botão "Imprimir Ficha" manual no card.
      for (const qItem of newlyArrivedQueueItems) {
        if (qItem.orderId) {
          db.deliveryOrders.get(qItem.orderId).then((order) => {
            if (order) {
              autoPrintKitchenLabel(order).then((r) => {
                if (r.ok) toast.success(`🖨️ Ficha cozinha #${order.id} impressa`);
              });
            }
          }).catch(() => {});
        }
      }
    }
    knownDeliveryIdsRef.current = deliveryIds;

    // v7.68 — Ding também para novos pedidos de MESA/comanda e para mudanças
    // de status vindas de outro device (o guard de 3s evita tocar quando a
    // mudança foi um clique local em markReady/markPreparing).
    if (knownStatusRef.current !== null && Date.now() - localActionAtRef.current > 3000) {
      let newNonDelivery = 0;
      let statusChanges = 0;
      for (const it of items) {
        const prev = knownStatusRef.current.get(it.id);
        if (prev === undefined) {
          if (it.source !== 'delivery') newNonDelivery += 1;
        } else if (prev !== it.status) {
          statusChanges += 1;
        }
      }
      if ((newNonDelivery > 0 || statusChanges > 0) && soundEnabled) playDing();
      if (newNonDelivery > 0) {
        toast.success(`🔔 ${newNonDelivery} novo${newNonDelivery > 1 ? 's' : ''} pedido${newNonDelivery > 1 ? 's' : ''} na cozinha!`);
      }
    }
    knownStatusRef.current = new Map(items.map((i) => [i.id, i.status]));

    setQueue(items);

    const groups = {};
    items.forEach((item) => {
      if (item.items) {
        item.items.forEach((i) => {
          const key = i.name;
          if (!groups[key]) groups[key] = { name: key, totalQty: 0, orders: [] };
          groups[key].totalQty += i.quantity;
          groups[key].orders.push({ queueId: item.id, orderId: item.orderId, source: item.source, createdAt: item.createdAt });
        });
      }
    });
    setGrouped(groups);
  }, [soundEnabled]);

  const toggleSound = () => {
    const next = !soundEnabled;
    setSoundEnabled(next);
    try { localStorage.setItem(SOUND_STORAGE_KEY, String(next)); } catch {}
    unlockAudio(); // desbloqueia AudioContext com o gesto
    if (next) {
      playDing();
      toast.success('Alerta sonoro ativado');
    } else {
      toast('Alerta sonoro desligado');
    }
  };

  // v7.71 — desbloqueia o áudio no primeiro toque em qualquer lugar da página
  // (política de autoplay). Sem isso o ding de novo pedido ficava mudo até
  // o cozinheiro clicar no toggle de som.
  useEffect(() => { installAutoUnlock(); }, []);

  // v7.73 — Tempo real: recarrega a fila na hora quando o Firebase mescla
  // mudanças de kdsQueue/deliveryOrders no Dexie (sem F5, sem esperar o
  // tick de 8s do polling). O ding de novo pedido dispara dentro do
  // loadQueue via detecção de itens novos.
  useEffect(() => {
    const onMerge = (e) => {
      const t = e?.detail?.table;
      if (t === 'kdsQueue' || t === 'deliveryOrders') loadQueue();
    };
    window.addEventListener('kaven:remote-merge', onMerge);
    return () => window.removeEventListener('kaven:remote-merge', onMerge);
  }, [loadQueue]);

  // Fase 27 — Visibility-aware polling (pausa em background, retoma ao focar).
  // v7.68 — Cozinha (KDS) usa ignoreVisibility: tela de produção não pode
  // perder pedidos novos só porque a aba não está em foco no momento.
  useVisiblePolling(loadQueue, 8000, { ignoreVisibility: true });

  // v7.81 — Watchdog de 15s: ressincroniza kdsQueue/deliveryOrders DIRETO do
  // Firebase (religa os listeners → snapshot completo → merge → evento →
  // loadQueue). Rede de segurança contra listener morto/socket caído — o
  // polling de 8s acima só relê o Dexie local e não recupera dados que o
  // listener perdeu. Roda mesmo com a aba em segundo plano (sujeito ao
  // throttling de timers do browser).
  useEffect(() => {
    const id = setInterval(() => {
      import('@/lib/firebaseSync')
        .then(({ resyncTablesFromRemote, resyncBackendOrders }) => { resyncTablesFromRemote(['kdsQueue', 'deliveryOrders']); resyncBackendOrders(); })
        .catch(() => {});
    }, 15000);
    return () => clearInterval(id);
  }, []);

  // TICKET 207625 - Recuperacao em tempo real ao voltar/focar a tela.
  // O watchdog de 15s acima usa setInterval, que o navegador ESTRANGULA (ou
  // pausa) com a aba em segundo plano; e o keepalive de conexao do firebaseSync
  // so faz goOnline() quando isConnected=false - nenhum deles RELIGA os
  // listeners de tabela quando o socket fica "meio-morto" ainda reportando
  // conectado. Resultado: o pedido chega ao Firebase mas a Cozinha so atualiza
  // apos um F5. Aqui forcamos um resync (religa listeners -> snapshot novo ->
  // merge -> loadQueue) no instante em que a tela volta a ficar visivel ou
  // recebe foco - mesmo efeito do F5, sem recarregar a pagina.
  useEffect(() => {
    const forceResync = () => {
      import('@/lib/firebaseSync')
        .then(({ resyncTablesFromRemote, resyncBackendOrders }) => { resyncTablesFromRemote(['kdsQueue', 'deliveryOrders']); resyncBackendOrders(); })
        .catch(() => {});
    };
    const onVis = () => { if (document.visibilityState === 'visible') forceResync(); };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', forceResync);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('focus', forceResync);
    };
  }, []);
  // Tick para recalcular urgência visual (≥20min) — sem chamar Dexie, só trigger render.
  useEffect(() => {
    const tickInterval = setInterval(() => setTick((t) => t + 1), 30000);
    return () => clearInterval(tickInterval);
  }, []);

  // v7.87 — Alerta de DEMORA: pedido há 30+ min sem ficar pronto → WhatsApp
  // dos admins (1x por pedido, flag `delayAlertAt` sincronizada entre devices).
  useEffect(() => {
    const DELAY_MS = 30 * 60 * 1000;
    const check = async () => {
      const now = Date.now();
      const stale = queue.filter(
        (q) => q.status !== 'ready' && !q.delayAlertAt && q.createdAt
          && now - new Date(q.createdAt).getTime() >= DELAY_MS
      );
      for (const item of stale) {
        // Releitura fresca: outro KDS pode ter alertado neste intervalo
        const fresh = await db.kdsQueue.get(item.id);
        if (!fresh || fresh.delayAlertAt || fresh.status === 'ready') continue;
        const nowIso = new Date().toISOString();
        localActionAtRef.current = Date.now();
        await db.kdsQueue.update(item.id, { delayAlertAt: nowIso, updatedAt: nowIso });
        const waitMin = Math.floor((now - new Date(item.createdAt).getTime()) / 60000);
        import('@/lib/operatorNotifications')
          .then(({ notifyStaffDelayedOrder }) => notifyStaffDelayedOrder(item, waitMin))
          .catch((e) => console.warn('[Z-API] notifyStaffDelayedOrder:', e));
      }
    };
    check();
  }, [queue, _tick]);

  // v7.92 — AUTO-REPARO (a cada tick de 30s): card já 'ready' na cozinha cuja
  // deliveryOrder ficou presa em status pré-despacho → promove pra fila do
  // entregador. Cobre: sync atrasado no markReady, falhas antigas (legado) e
  // pedidos promovidos por outro caminho. Releitura fresca evita corrida
  // entre múltiplas telas KDS; nunca toca em pedidos entregues/cancelados.
  useEffect(() => {
    const heal = async () => {
      const cutoff = new Date(Date.now() - 24 * 3600 * 1000).toISOString();
      const readyCards = await db.kdsQueue
        .filter((q) => q.status === 'ready' && q.source === 'delivery' && !!q.orderId && (q.createdAt || '') >= cutoff)
        .toArray();
      for (const card of readyCards) {
        const order = await findDeliveryOrder(card.orderId);
        if (!order) continue;
        if (['ready', 'delivering', 'delivered', 'cancelled'].includes(order.status)) continue;
        const fresh = await db.deliveryOrders.get(order.id);
        if (!fresh || ['ready', 'delivering', 'delivered', 'cancelled'].includes(fresh.status)) continue;
        localActionAtRef.current = Date.now();
        console.warn(`[KDS AUTO-REPARO] pedido #${order.id} estava 'pronto' na cozinha mas preso em '${fresh.status}' — promovendo pra fila do entregador`);
        await promoteOrderToReady(fresh, card);
      }
    };
    heal().catch(() => {});
  }, [_tick]); // eslint-disable-line react-hooks/exhaustive-deps

  // v7.92 — Resolve a deliveryOrder de forma robusta (id numérico/string).
  const findDeliveryOrder = async (orderId) => {
    let order = await db.deliveryOrders.get(orderId);
    if (!order) {
      order = await db.deliveryOrders
        .filter((o) => String(o.id) === String(orderId))
        .first()
        .catch(() => null);
    }
    return order || null;
  };

  // v7.92 — Promoção ATÔMICA pra fila do entregador: Dexie + push explícito
  // Firebase + servidor (Mongo) + alerta WhatsApp. Retorna false se o push
  // remoto falhou (ficou enfileirado pra reenvio automático).
  const promoteOrderToReady = async (order, queueItem, { notify = true } = {}) => {
    const now = new Date().toISOString();
    // v7.94 — limpa atribuição residual: pedido PRONTO não pertence a
    // nenhum entregador ainda (rota revertida deixava assignedTo preso).
    await db.deliveryOrders.update(order.id, { status: 'ready', updatedAt: now, assignedTo: null, assignedName: null });
    let pushOk = true;
    // v8.08 — DIAGNÓSTICO "Rede lenta": as vias de rede rodavam em SÉRIE
    // (push order 5s + push kdsQueue 5s + POST HTTP 6s = até 16s) contra um
    // timeout de UI de 5s — em 4G fraco o alerta disparava mesmo com tudo
    // funcionando. Agora as 3 vias rodam EM PARALELO (pior caso real = 6s).
    try {
      const [{ pushToRemote }, { upsertDispatchOrder }] = await Promise.all([
        import('@/lib/firebaseSync'),
        import('@/lib/orderServerSync'),
      ]);
      const updatedOrder = await db.deliveryOrders.get(order.id);
      const updatedQueue = queueItem ? await db.kdsQueue.get(queueItem.id) : null;
      const jobs = [];
      if (updatedOrder) jobs.push(pushToRemote('deliveryOrders', order.id, updatedOrder));
      if (updatedQueue) jobs.push(pushToRemote('kdsQueue', queueItem.id, updatedQueue));
      // PONTE HTTP OBRIGATÓRIA (padrão do pagamento): o polling do
      // entregador puxa daqui mesmo com sockets hibernados.
      const dispatchJob = upsertDispatchOrder(updatedOrder || order, 'ready');
      const [pushResults, dispatched] = await Promise.all([Promise.all(jobs), dispatchJob]);
      const fbOk = !pushResults.some((r) => r && r.ok === false);
      const httpOk = !!dispatched?.ok;
      if (!httpOk) {
        console.warn('[KDS] dispatch HTTP falhou — Firebase segue como via paralela:', dispatched?.reason);
      }
      // Sucesso se QUALQUER via entregou (as duas são independentes)
      pushOk = fbOk || httpOk;
    } catch (e) {
      console.warn('[KDS] push/dispatch falhou (enfileirado p/ reenvio):', e);
      pushOk = false;
    }
    if (notify) {
      import('@/lib/operatorNotifications')
        .then(({ notifyStaffOrderReady }) => notifyStaffOrderReady({ ...order, id: order.id }))
        .catch((e) => console.warn('[Z-API] notifyStaffOrderReady:', e));
    }
    return pushOk;
  };

  const TERMINAL_STATUSES = ['delivered', 'cancelled'];
  const DISPATCH_STATUSES = ['ready', 'delivering'];

  const markReady = async (queueItem) => {
    // v8.04 — bloqueio imediato de cliques duplos/triplos: o fluxo tem
    // awaits longos (resync/push) e o operador clicava de novo no atraso.
    if (processingIds.has(queueItem.id)) return;
    setProcessingIds((s) => new Set(s).add(queueItem.id));
    try {
      // v8.08 — timeout de UI de 8s (> pior caso real de 6s das vias em
      // paralelo): destrava o botão em falha REAL, sem falso alarme de
      // "Rede lenta" quando a rede móvel só está mais devagar.
      await Promise.race([
        markReadyInner(queueItem),
        new Promise((_, rej) => setTimeout(() => rej(new Error('kds_ui_timeout')), 8000)),
      ]);
    } catch (err) {
      if (String(err?.message) === 'kds_ui_timeout') {
        toast.error('Rede lenta — ação salva no aparelho.', {
          description: 'Será reenviada automaticamente. Se o pedido não sair da fila, toque em "Pronto" novamente.',
          duration: 6000,
        });
      } else {
        toast.error('Erro de rede. Clique novamente para reenviar.');
      }
    } finally {
      setProcessingIds((s) => { const n = new Set(s); n.delete(queueItem.id); return n; });
    }
  };

  const markReadyInner = async (queueItem) => {
    const now = new Date().toISOString();
    localActionAtRef.current = Date.now();
    await db.kdsQueue.update(queueItem.id, { status: 'ready', readyAt: now, updatedAt: now });
    let syncFailed = false;
    if (queueItem.source === 'delivery' && queueItem.orderId) {
      // v7.92 — FIX do gatilho: antes só promovia se status === 'preparing';
      // qualquer outro status (ou pedido ainda não sincronizado neste
      // aparelho) falhava EM SILÊNCIO e o entregador nunca via o pedido.
      let order = await findDeliveryOrder(queueItem.orderId);
      if (!order) {
        // Pedido pode não ter chegado neste aparelho ainda: força resync e retenta
        try {
          const { resyncTablesFromRemote } = await import('@/lib/firebaseSync');
          await resyncTablesFromRemote(['deliveryOrders']);
          await new Promise((r) => setTimeout(r, 1500));
          order = await findDeliveryOrder(queueItem.orderId);
        } catch {}
      }
      if (!order) {
        // O auto-reparo (efeito abaixo) promove sozinho assim que sincronizar
        toast.warning('Pronto na cozinha — aguardando sincronizar o registro do delivery', {
          description: 'A promoção pra tela do entregador acontece automaticamente em instantes.',
          duration: 6000,
        });
        syncFailed = true;
      } else if (TERMINAL_STATUSES.includes(order.status)) {
        // Blindagem: pedido entregue/cancelado NUNCA volta pro fluxo
      } else if (!DISPATCH_STATUSES.includes(order.status)) {
        const pushOk = await promoteOrderToReady(order, queueItem);
        syncFailed = syncFailed || !pushOk;
      }
    }
    if (syncFailed) {
      toast.warning('Pedido pronto salvo, mas AINDA NÃO sincronizado', {
        description: 'Sem conexão com o servidor de sync. A atualização foi enfileirada e será reenviada automaticamente ao reconectar.',
        duration: 8000,
      });
    } else {
      toast.success('Pedido pronto!');
    }
    loadQueue();
  };

  const markPreparing = async (queueItem) => {
    localActionAtRef.current = Date.now();
    await db.kdsQueue.update(queueItem.id, { status: 'preparing' });
    toast.success('Preparando...');
    loadQueue();
  };

  // Fase 27 — derivações memoizadas (evita recriar arrays a cada tick/render)
  const pendingItems = useMemo(() => queue.filter((q) => q.status === 'pending'), [queue]);
  const preparingItems = useMemo(() => queue.filter((q) => q.status === 'preparing'), [queue]);
  const groupedArr = useMemo(
    () => Object.values(grouped).sort((a, b) => b.totalQty - a.totalQty),
    [grouped]
  );
  const sortedQueue = useMemo(
    () => [...queue].sort((a, b) => (a.createdAt || '').localeCompare(b.createdAt || '')),
    [queue]
  );

  return (
    <div data-testid="kds-page" className="p-6 lg:p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="font-heading text-3xl tracking-tight font-bold text-zinc-900">Cozinha (KDS)</h2>
          <p className="text-sm text-zinc-500 mt-1">Tela de Producao</p>
        </div>
        <div className="flex gap-3 items-center flex-wrap">
          <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-sm px-3 py-1">
            {pendingItems.length} pendentes
          </Badge>
          <Badge className="bg-blue-100 text-blue-700 border-blue-200 text-sm px-3 py-1">
            {preparingItems.length} preparando
          </Badge>
          <button
            data-testid="kds-sound-toggle"
            onClick={toggleSound}
            title={soundEnabled ? 'Alerta sonoro ativo — clique para silenciar' : 'Silenciado — clique para ativar'}
            className={`flex items-center gap-1.5 px-3 h-10 rounded-lg border-2 transition-all active:translate-y-[1px] font-bold text-xs ${
              soundEnabled
                ? 'bg-emerald-50 border-emerald-300 text-emerald-700 hover:bg-emerald-100'
                : 'bg-white border-zinc-300 text-zinc-500 hover:border-zinc-500'
            }`}
          >
            {soundEnabled ? <Volume2 size={14} /> : <VolumeX size={14} />}
            {soundEnabled ? 'Som Ativo' : 'Silenciado'}
          </button>
          <button data-testid="kds-refresh-btn" onClick={loadQueue} className="p-2 rounded-lg border-2 border-zinc-200 hover:border-zinc-900 transition-all active:translate-y-[1px] min-w-[40px] min-h-[40px] flex items-center justify-center">
            <Clock size={16} />
          </button>
        </div>
      </div>

      {/* Grouped Summary */}
      {groupedArr.length > 0 && (
        <div className="mb-8">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-400 mb-4">Resumo por Item</p>
          <div className="flex flex-wrap gap-3">
            {groupedArr.map((g) => (
              <div key={g.name} data-testid={`kds-summary-${g.name}`} className="bg-white border-2 border-zinc-200 rounded-xl px-5 py-3 flex items-center gap-3">
                <span className="font-mono text-2xl font-black text-[#2563eb]">{g.totalQty}</span>
                <span className="text-sm font-semibold text-zinc-700">{g.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Order Cards */}
      <div data-testid="kds-grid" translate="no" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {sortedQueue.map((item) => {
          const wait = getWaitMinutes(item.createdAt);
          const urgency = getUrgencyColor(wait);
          // Fase 17 — Urgência CRÍTICA: pendente há > 20 minutos pulsa vermelho
          const isCritical = wait >= 20 && item.status !== 'ready';
          const cardBorder = isCritical
            ? 'border-red-500 ring-2 ring-red-300 animate-pulse shadow-lg shadow-red-300/50'
            : item.status === 'preparing'
              ? 'border-blue-400'
              : 'border-zinc-200';
          return (
            <div
              key={item.id}
              data-testid={`kds-order-${item.id}`}
              data-critical={isCritical ? 'true' : 'false'}
              className={`bg-white border-2 rounded-xl p-5 transition-all ${cardBorder}`}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  {item.source === 'delivery' ? <Bike size={16} className="text-[#2563eb]" /> : <ChefHat size={16} className="text-zinc-400" />}
                  <span className="text-xs uppercase tracking-wider font-bold text-zinc-400">
                    {item.source === 'delivery' ? 'Delivery' : 'Local'}
                  </span>
                  {isCritical && (
                    <span
                      data-testid={`kds-critical-badge-${item.id}`}
                      className="ml-1 flex items-center gap-0.5 text-[10px] uppercase tracking-wider font-bold text-red-600 bg-red-50 border border-red-200 px-1.5 py-0.5 rounded"
                    >
                      <AlertTriangle size={9} strokeWidth={2.5} />
                      URGENTE
                    </span>
                  )}
                </div>
                <div className={`flex items-center gap-1 px-2 py-1 rounded-lg border text-xs font-bold ${urgency}`}>
                  <Clock size={12} />
                  {wait}min
                </div>
              </div>

              <p className="font-bold text-zinc-900 text-sm mb-1">
                {item.source === 'delivery' ? item.customerName || `Pedido #${item.orderId}` : `Mesa ${item.tableNumber || '--'}`}
              </p>

              <div className="space-y-1 mt-3">
                {item.items?.map((i, idx) => (
                  <div key={idx} className="flex justify-between text-sm">
                    <span className="text-zinc-700">{i.name}</span>
                    <span className="font-mono font-bold text-zinc-900">{i.quantity}x</span>
                  </div>
                ))}
              </div>

              <div className="mt-4 flex gap-2">
                {item.status === 'pending' && (
                  <button data-testid={`kds-start-${item.id}`} onClick={() => markPreparing(item)} className="flex-1 flex items-center justify-center gap-2 bg-amber-500 text-white rounded-lg font-bold py-3 min-h-[44px] active:translate-y-[1px] transition-all text-sm">
                    <ChefHat size={16} /> Preparar
                  </button>
                )}
                {(() => {
                  const isProcessing = processingIds.has(item.id);
                  return (
                    <button
                      data-testid={`kds-ready-${item.id}`}
                      onClick={() => markReady(item)}
                      disabled={isProcessing}
                      className="flex-1 flex items-center justify-center gap-2 bg-emerald-600 text-white rounded-lg font-bold py-3 min-h-[44px] active:translate-y-[1px] transition-all text-sm disabled:opacity-60 disabled:cursor-wait"
                    >
                      {isProcessing ? (
                        <><Loader2 size={16} className="animate-spin" /> Processando...</>
                      ) : (
                        <><CheckCircle size={16} /> Pronto</>
                      )}
                    </button>
                  );
                })()}
                {item.source === 'delivery' && item.orderId && (
                  <button
                    data-testid={`kds-print-${item.id}`}
                    onClick={async () => {
                      try {
                        const order = await db.deliveryOrders.get(item.orderId);
                        if (!order) return toast.error('Pedido não encontrado');
                        await printKitchenLabel(order);
                        toast.success(`Ficha #${order.id} enviada para impressão`);
                      } catch (err) {
                        toast.error(err.message || 'Falha ao imprimir');
                      }
                    }}
                    className="flex items-center justify-center gap-1 bg-slate-900 text-white rounded-lg font-bold px-3 min-h-[44px] active:translate-y-[1px] transition-all text-xs"
                    title="Imprimir ficha de cozinha"
                  >
                    <Printer size={14} />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {queue.length === 0 && (
        <div className="text-center py-20 text-zinc-300">
          <ChefHat size={64} className="mx-auto mb-4 opacity-30" />
          <p className="text-lg font-semibold">Nenhum pedido na fila</p>
          <p className="text-sm mt-1">Os pedidos aparecerao aqui automaticamente</p>
        </div>
      )}
    </div>
  );
}
